import { inject, Injectable, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  AbstractControl,
  Validators,
  FormArray,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  CheckboxGroupModel,
  SelectListItem,
  TranslationModel,
  ValidationModel,
  ValidationType,
} from '@shared/models';
import { AuthService, ServiceBase, SnackbarFactory } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import {
  ConstrainedField,
  createConstrainedField,
  toFormGroup,
} from '@app/shared/models/constrained-field';
import { DragAndDropList } from '@app/shared/models/dnd';
import { DynamicListExtraData } from './cells/dynamic-list/dynamic-list.component';
import { SelectListControlExtraData } from './cells/selectlist-control-cell/selectlist-control-cell.component';

@Injectable({ providedIn: 'root' })
export abstract class DynamicEditorBase implements OnInit {
  protected snackBar: SnackbarFactory = inject(SnackbarFactory);
  protected auth: AuthService = inject(AuthService);
  protected translateService: TranslateService = inject(TranslateService);

  public id: string;
  public operation: string;
  public data: any;
  public isEditing: boolean;

  public formGroup: FormGroup<{ [key: string]: AbstractControl<any, any> }>;
  public isLoaded = false;
  public title: string;
  public idName: string = 'id';

  protected validationModel: ValidationModel[] = [];
  public constrainedFields: Array<ConstrainedField> = [];
  public isHttpRequestSent: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DynamicEditorBase>,
    data: {
      id: string | number;
      idName: string;
      operation: string;
      validationModel?: ValidationModel[];
    },
    protected service: ServiceBase<any>,
    protected useCreateGet: boolean = true,
    protected isNumericId: boolean = true
  ) {
    this.id = data.id?.toString() ?? null;
    this.operation = data.operation;
    this.isEditing = this.operation === 'U';
    this.title = this.isEditing ? 'edit' : 'create';
    dialogRef.disableClose = true;

    this.idName = data.idName;
    this.validationModel = data.validationModel ?? [];
    this.data = null;
  }

  public ngOnInit(): void {
    this.getValidation();
  }

  public getValidation(extraParams?: object): void {
    if (this.validationModel.length == 0) {
      let param = { operation: this.operation, ...extraParams };
      this.isEditing
        ? (param = {
            [this.idName]: this.isNumericId
              ? parseInt(this.id)
              : this.id.toString(),
            ...param,
          })
        : null;
      this.service.getValidation(param).subscribe({
        next: (validationModel) => {
          this.validationModel = validationModel;
          this.loadData();
        },
        error: (err) => {
          this.snackBar.showButtonSnackbar(err);
        },
      });
    } else {
      this.loadData();
    }
  }

  public loadData(extraParams?: object): void {
    let handleCreateFormGroup = () => {
      if (this.validationModel.every((vm) => vm.sort != null)) {
        this.validationModel = this.validationModel.sort(
          (a, b) => a.sort - b.sort
        );
      }
      const fields = this.createConstrainedModel(
        this.validationModel,
        this.data
      );
      this.constrainedFields = fields;
      this.formGroup = toFormGroup(fields);
    };

    let handleSubscription = {
      next: (r) => {
        this.data = r[0];
        handleCreateFormGroup();
      },
      error: (err) => {
        this.responseHandler(err, null);
      },
    };

    if (this.isEditing) {
      this.service
        .updateGet({
          [this.idName]: this.isNumericId
            ? parseInt(this.id)
            : this.id.toString(),
          ...extraParams,
        })
        .subscribe(handleSubscription);
    } else {
      if (this.useCreateGet) {
        this.service
          .createGet({ ...extraParams })
          .subscribe(handleSubscription);
      } else {
        this.data = null;
        handleCreateFormGroup();
      }
    }
  }

  public createConstrainedModel(
    validationModel: ValidationModel[],
    data?: any
  ): ConstrainedField[] {
    let constrainedFields = createConstrainedModel(validationModel, data);
    this.setConstrainedFieldsConnections(constrainedFields);
    return constrainedFields;
  }

  public setConstrainedFieldsConnections(
    constrainedFields: ConstrainedField[]
  ) {}

  public readForm(): Record<string, any> {
    let readValue = {};

    let keys = Object.keys(this.formGroup.controls);

    keys.forEach((key) => {
      let formControl = this.formGroup.controls[key];
      let cf = this.constrainedFields.find((cf) => cf.key == key);
      const value = formControl.getRawValue();

      //for "translated" type multi-key data handling:
      if (
        cf &&
        cf?.controlType == 'translated' &&
        typeof value == 'object' &&
        !Array.isArray(value)
      ) {
        readValue = { ...readValue, ...value };
      } else {
        readValue[key] = value;
      }
    });

    return readValue;
  }

  onSubmit(): void {
    if (!this.formGroup.valid) return;

    const result = this.readForm();

    let responseHandle = {
      next: (r) => {
        this.isHttpRequestSent = false;
        this.responseHandler(r, result);
      },
      error: (err) => {
        this.isHttpRequestSent = false;
        this.responseHandler(err, result);
      },
    };

    this.isHttpRequestSent = true;

    if (this.isEditing) {
      this.service.update(this.id, result).subscribe(responseHandle);
    } else {
      this.service.create(result).subscribe(responseHandle);
    }
  }

  responseHandler(r, result) {
    if (r.status >= 200 && r.status < 300) {
      this.snackBar.showDurationSnackbar(r);
      this.dialogRef.close({ success: true, result });
    } else if (r.status >= 400 && r.status < 500) {
      this.snackBar.showButtonSnackbar(r);
    } else {
      this.dialogRef.close(true);
    }
  }

  public hasRight(right): boolean {
    return this.auth.isAuthenticated(right);
  }

  public hasNoRight(right): boolean {
    return !this.hasRight(right);
  }
}

export function createConstrainedModel(
  validationModel: ValidationModel[],
  data?: any
): ConstrainedField[] {
  const fields: ConstrainedField[] = [];
  const dynamicListFields: string[] = validationModel
    .filter((vm) => vm.fType == 'dynamicList')
    .map((vm) => `${vm.field}_`);

  for (let valModel of validationModel) {
    if (dynamicListFields.some((field) => valModel.field.startsWith(field))) {
      continue;
    }

    const fieldName = valModel.field;

    let initialVal = data ? data[fieldName] : null;

    if (valModel.fType === 'boolean') initialVal = initialVal ?? false;
    if (valModel.fType === 'time') initialVal = initialVal ?? '00:00:00';
    if (valModel.fType === 'ddlMultiple') {
      if (
        (initialVal == null || initialVal == '[]') &&
        !Array.isArray(initialVal)
      ) {
        initialVal = [];
      } else if (initialVal) {
        if (
          typeof initialVal == 'number' ||
          (typeof initialVal == 'string' &&
            !(initialVal.includes('[') || initialVal.includes(']')))
        ) {
          initialVal = [initialVal];
        } else if (
          typeof initialVal == 'string' &&
          initialVal.includes('[') &&
          initialVal.includes(']')
        ) {
          initialVal = JSON.parse(initialVal);
        }
      }
    }
    if (valModel.fType === 'dnd')
      initialVal =
        (data[fieldName] as DragAndDropList).selectedFields
          ?.map((field) => field.id)
          .join(',') ?? null;
    if (
      (valModel.fType === 'date' || valModel.fType === 'datetime') &&
      typeof initialVal === 'string'
    )
      initialVal = new Date(initialVal);

    const control = createFormControl(valModel, initialVal, data);
    const extraData = createExtraDataPayload(
      valModel,
      validationModel,
      valModel.fType,
      data
    );

    const constrainedField = createConstrainedField({
      key: fieldName,
      controlType: valModel.fType,
      control,
      // Translate fields contained within the FormArrays are always enabled.
      // Editable here only refers to the translation key, which is handled inside
      // the translated-cell component.
      initiallyDisabled:
        valModel.fType !== 'translated' ? !valModel.fEditable : false,
      editable: valModel.fType !== 'translated' ? valModel.fEditable : true,
      hidden: valModel.fHide,
      tooltip: data?.[`${fieldName}_tooltip`] ?? null,
      validationParams: {
        required: valModel.fMandatory,
        min: valModel?.fParams?.min ?? 0,
        max: valModel?.fParams?.max ?? 1_000_000,
      },
      extraData,
    });

    fields.push(constrainedField);
  }

  return fields;
}

export function createFormControl(
  valModel: ValidationModel,
  initialVal: any,
  data?: any
): AbstractControl {
  if (valModel.fType === 'translated') {
    let val = data?.[`${valModel.field}_Dic`];
    const translationModel =
      typeof val == 'object' ? (val as TranslationModel[]) : null;

    let translatedFormGroup: Record<string, AbstractControl> = {};
    translatedFormGroup[valModel.field] = new FormControl({
      value: initialVal,
      disabled: !valModel.fEditable,
    });

    valModel.fParams.languages.forEach((lang) => {
      translatedFormGroup[`${valModel.field}_${lang}`] = new FormControl({
        value:
          translationModel?.length > 0
            ? translationModel?.find((tm) => tm.lang == lang)?.text
            : null,
        disabled: !(valModel.fParams?.fEditableDic ?? true),
      });
    });
    return new FormGroup(translatedFormGroup);
  }
  if (valModel.fType === 'file') {
    let fileformGroup: Record<string, AbstractControl> = {};
    const fieldName = valModel.field;

    fileformGroup[fieldName] = new FormControl(
      { value: null, disabled: !valModel.fEditable },
      valModel.fMandatory ? Validators.required : null
    );
    fileformGroup[`${fieldName}Name`] = new FormControl({
      value: null,
      disabled: true,
    });
    fileformGroup[`${fieldName}UploadName`] = new FormControl({
      value: null,
      disabled: true,
    });

    return new FormGroup(fileformGroup);
  }
  if (valModel.fType === 'checkboxGroup') {
    let checkBoxes: Record<string, AbstractControl> = {};
    let checkBoxesData = (data?.[valModel.field] as CheckboxGroupModel[]) ?? [];

    checkBoxesData?.forEach((checkBox) => {
      let checkControl = new FormControl({
        value: checkBox.value ?? false,
        disabled: !valModel.fEditable,
      });
      checkBoxes[checkBox.id] = checkControl;
    });

    return new FormGroup(checkBoxes);
  }
  if (valModel.fType === 'checkboxList') {
    let checkBoxes: Record<string, AbstractControl> = {};
    let checkBoxesData = (data?.[valModel.field] as SelectListItem[]) ?? [];

    checkBoxesData?.forEach((checkBox) => {
      checkBoxes[checkBox.id.toString()] = new FormControl({
        value: true,
        disabled: !valModel.fEditable,
      });
    });

    return new FormGroup(checkBoxes);
  }
  if (valModel.fType === 'dynamicList') {
    return new FormArray([]);
  }

  return new FormControl({ value: initialVal, disabled: !valModel.fEditable });
}

export function createExtraDataPayload(
  valModel: ValidationModel,
  allValModel: ValidationModel[],
  controlType: ValidationType,
  data?: any
): any {
  if (
    (
      [
        'ddl',
        'ddlMultiple',
        'ddlSearch',
        'search',
        'checkboxList',
      ] as ValidationType[]
    ).includes(controlType)
  ) {
    return valModel.fParams?.listName
      ? { listName: valModel.fParams?.listName }
      : null;
  }

  if (controlType === 'date') {
    let extraData: any = {};
    extraData.allowedDays = valModel.fParams?.allowedDays ?? null;
    return extraData;
  }

  if (controlType === 'ddlControl') {
    let extraData: SelectListControlExtraData = {
      params: valModel.fParams?.listName
        ? { listName: valModel.fParams?.listName }
        : null,
      validationModels: allValModel,
    };
    return extraData;
  }

  if (controlType === 'dnd') {
    return data[valModel.field] as DragAndDropList;
  }

  if (controlType === 'translated') {
    return valModel.fParams?.languages ?? [];
  }

  if (controlType === 'checkboxGroup') {
    let checkBoxTexts = {};
    (data[valModel.field] as CheckboxGroupModel[])?.forEach((checkBox) => {
      checkBoxTexts[checkBox.id] = checkBox.text;
    });
    return checkBoxTexts;
  }

  if (controlType == 'dynamicList') {
    let validationModel = allValModel
      .filter((vm) => vm.field.startsWith(`${valModel.field}_`))
      .map((vm) => {
        return { ...vm, field: vm.field.split(`${valModel.field}_`)[1] };
      });
    let extraData: DynamicListExtraData = {
      data:
        data?.[valModel.field] && data?.[valModel.field] != '[]'
          ? data[valModel.field]
          : null,
      validationModel,
    };
    return extraData;
  }

  return null;
}
